import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import {
  getAttendanceAnomalies,
  reviewAttendanceAnomaly
} from '../../services/api/implementation/impl';
import { formatHours } from './formatHours';
import { Callout } from '../../../shared/components/ui/primitives';

/**
 * Days whose arithmetic went wrong.
 *
 * These were already computed when each day was totalled and thrown away unless
 * a supervisor happened to open that workman's timeline. Every row here is a day
 * where somebody may have been under- or over-paid.
 *
 * Marking one reviewed deliberately changes no number. If the hours are wrong
 * the fix is to correct the punches, which recomputes the day and re-raises
 * anything still wrong — a review that silently adjusted a figure would be a
 * wage adjustment wearing a different name.
 */

const EXPLANATIONS = {
  OVERLAPPING_SESSIONS: {
    title: 'Two segments overlap',
    body:
      'The same workman was marked on two pieces of work at once. The shared time was paid once, not twice — but one of the two entries is wrong about who was where.'
  },
  OUT_BEFORE_IN: {
    title: 'A punch-out came before its punch-in',
    body:
      'That segment was dropped rather than being counted backwards, so worked time is missing from this day. Someone needs to re-enter it.'
  },
  MULTIPLE_OPEN_SESSIONS: {
    title: 'More than one segment left open',
    body:
      'Two punch-ins with no punch-out between them. The day is still counting up and will keep doing so until it is closed.'
  },
  OT_CAPPED: {
    title: 'Overtime hit the daily cap',
    body:
      'More overtime was worked than the policy allows in one day, so it was trimmed. Either the punch is wrong, or the cap needs raising.'
  }
};

const AttendanceAnomalies = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=labour' });
  const queryClient = useQueryClient();
  const [includeReviewed, setIncludeReviewed] = useState(false);

  const { data, isFetching } = useQuery({
    queryKey: ['attendanceAnomalies', includeReviewed],
    queryFn: () =>
      getAttendanceAnomalies(includeReviewed ? { include_reviewed: 'true' } : {})
  });

  const review = useMutation({
    mutationFn: (attendanceId) => reviewAttendanceAnomaly(attendanceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendanceAnomalies'] });
      toast.success('Marked as reviewed');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not mark it reviewed')
  });

  const rows = data?.data ?? [];

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Attendance Anomalies' }} />

      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Callout tone="info" collapsible icon={<Info size={14} />} title="What an anomaly is" className="min-w-0 flex-1 sm:max-w-2xl">
            Days whose hours didn&apos;t add up — someone may be under- or over-paid. Marking reviewed changes nothing; fix the punches on the muster to recompute.
          </Callout>
          <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={includeReviewed}
              onChange={(e) => setIncludeReviewed(e.target.checked)}
              className="h-3.5 w-3.5 accent-brand-700"
            />
            Include already reviewed
          </label>
        </div>

        {isFetching ? (
          <Loader />
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white py-10 text-center">
            <Check size={28} className="mx-auto text-success-fg" />
            <p className="mt-2 text-sm text-gray-600">
              {includeReviewed
                ? 'Nothing has been flagged.'
                : 'Nothing outstanding — every flagged day has been looked at.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => (
              <div
                key={row.attendance_id}
                className={`rounded-lg border bg-white p-4 ${
                  row.reviewed_at ? 'border-gray-200 opacity-70' : 'border-warning-border'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <AlertTriangle
                        size={15}
                        className={row.reviewed_at ? 'text-gray-400' : 'text-warning-fg'}
                      />
                      <span className="font-medium text-gray-900">
                        {row.worker?.name ?? 'Unknown workman'}
                      </span>
                      <span className="text-xs text-gray-500">{row.date}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {[
                        row.project?.name,
                        row.worker?.trade,
                        formatHours(row.working_hours),
                        row.overtime_hours ? `${row.overtime_hours}h OT` : null,
                        row.marked_by ? `recorded by ${row.marked_by}` : null
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>

                  {row.reviewed_at ? (
                    <span className="shrink-0 rounded bg-gray-100 px-2 py-1 text-xs text-gray-600">
                      Reviewed
                    </span>
                  ) : (
                    <button
                      type="button"
                      disabled={review.isPending}
                      onClick={() => review.mutate(row.attendance_id)}
                      className="shrink-0 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 disabled:opacity-50"
                    >
                      Mark reviewed
                    </button>
                  )}
                </div>

                <div className="mt-3 space-y-2">
                  {row.anomalies.map((code) => {
                    const info = EXPLANATIONS[code];
                    return (
                      <div key={code} className="rounded bg-gray-50 px-3 py-2">
                        <div className="text-xs font-semibold text-gray-800">
                          {info?.title ?? code}
                        </div>
                        {info?.body && (
                          <div className="mt-0.5 text-xs text-gray-600">{info.body}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AttendanceAnomalies;
